import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi } from '../lib/api';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cartApi.get();
      setCart(response.data.cart);
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
      setItemCount(response.data.item_count || 0);
    } catch (err) {
      console.error('Error fetching cart:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (item) => {
    try {
      setLoading(true);
      await cartApi.add(item);
      await fetchCart();
      return true;
    } catch (err) {
      console.error('Error adding to cart:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (variantSku, quantity) => {
    try {
      setLoading(true);
      await cartApi.update(variantSku, quantity);
      await fetchCart();
      return true;
    } catch (err) {
      console.error('Error updating cart:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (variantSku) => {
    try {
      setLoading(true);
      await cartApi.remove(variantSku);
      await fetchCart();
      return true;
    } catch (err) {
      console.error('Error removing from cart:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      await cartApi.clear();
      await fetchCart();
      return true;
    } catch (err) {
      console.error('Error clearing cart:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    cart,
    items,
    total,
    itemCount,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    fetchCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
